import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@langchain/untitled-ui-icons';
import { AxisTop } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { scaleOrdinal, scaleTime } from '@visx/scale';
import { Bar } from '@visx/shape';
import { Text } from '@visx/text';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRunTypeColors, useTextColor } from '@/components/RunType.utils';
import { RunTypeIcon } from '@/components/RunTypeIcon';
import { useColorScheme } from '@/hooks/useColorScheme';
import { RunSchema } from '@/types/schema';
import { useResizeObserver } from '@/utils/use-resize-observer';

import { useWaterfallZoomState } from '../store/useWaterfallZoomState';
import {
  useBackgroundFill,
  useBarDimensions,
  useRunInfoDimensions,
  useScrollPosition,
} from '../utils/performancePanelHooks';
import { useRunsInTreeStructure } from '../utils/useRunsInTreeStructure';
import { calculateMinMaxTimeRanges } from '../utils/utils';
import {
  GRAPH_RUN_ICON_DIMENSION,
  LATENCY_GRAPH_HEIGHT_TOP_OFFSET,
  TREE_COLLAPSED_NODE_HEIGHT,
} from './constants';

export function WaterfallLatencyBar({
  run,
  index,
  xScale,
  yScale,
  onClick,
  isActive,
  isActiveDescendant,
  isFiltered,
  svgWidth,
  hasChildren,
  hasChildrenVisible,
  setFoldedRuns,
  containerWidth,
  debouncedScrollLeft,
}: {
  run: RunSchema;
  index: number;
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleOrdinal<number, number>>;

  isActive: boolean;
  isActiveDescendant: boolean;
  isFiltered: boolean;
  onClick: () => void;
  minStartTime: number;
  maxEndTime: number;
  svgWidth: number;
  hasChildren: boolean;
  hasChildrenVisible: boolean;
  setFoldedRuns: React.Dispatch<React.SetStateAction<string[]>>;
  containerWidth: number;
  debouncedScrollLeft: number;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const { barWidth, barHeight, barYPosition, barXPosition } = useBarDimensions({
    run,
    index,
    xScale,
    yScale,
  });

  const {
    iconX,
    textX,
    textY,
    runText,
    iconY,
    estimatedInfoWidth,
    estimatedTextWidth,
  } = useRunInfoDimensions({
    run,
    barHeight,
    barXPosition,
    barYPosition,
    svgWidth: svgWidth - 30,
  });

  const {
    bg,
    hover,
    hoverBorder,
    active,
    icon: iconColor,
  } = useRunTypeColors(run.run_type, run.name);

  const barColor = isActive ? active : isHovered ? hover : bg;
  const barStroke = isHovered ? hoverBorder : 'transparent';

  const willOverflow = estimatedInfoWidth > barWidth;
  const textColor = useTextColor(willOverflow, isActive, isFiltered);
  const backgroundFill = useBackgroundFill(
    isHovered,
    isActive,
    isActiveDescendant
  );

  const CollapseIcon = hasChildrenVisible ? ChevronDownIcon : ChevronRightIcon;

  // Calculate the fixed position for the collapse icon
  const collapseIconX =
    containerWidth - GRAPH_RUN_ICON_DIMENSION - 8 + debouncedScrollLeft;

  function getCollapseIconStroke() {
    if (isHovered || isActive) {
      return 'hsla(var(--ls-gray-100))';
    }
    return 'transparent';
  }

  return (
    barWidth >= 0 &&
    barHeight > 0 && (
      <g
        className="group cursor-pointer"
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {xScale.range()[1] > 0 && (
          <rect
            className="transition-all duration-300 ease-in-out"
            x={0}
            y={yScale(index) ?? 0}
            width={svgWidth}
            height={TREE_COLLAPSED_NODE_HEIGHT}
            fill={backgroundFill}
          />
        )}
        {isActive && (
          <path
            d={`M 0 ${yScale(index)} L 0 ${
              yScale(index) + TREE_COLLAPSED_NODE_HEIGHT
            }`}
            stroke="var(--bg-brand-primary)"
            strokeWidth={2}
          />
        )}

        <Bar
          className="cursor-pointer transition-[fill] duration-300 ease-in-out"
          x={barXPosition}
          y={barYPosition}
          width={barWidth + 1}
          height={barHeight}
          fill={barColor}
          stroke={barStroke}
          rx={4}
        />

        {isFiltered && (
          <rect
            x={textX - 2}
            y={iconY}
            width={estimatedTextWidth + 2 * 2}
            height={GRAPH_RUN_ICON_DIMENSION}
            fill="#FFDC41"
            stroke="#FFDC41"
          />
        )}
        <RunTypeIcon
          run={run}
          x={iconX}
          y={iconY}
          width={GRAPH_RUN_ICON_DIMENSION}
          height={GRAPH_RUN_ICON_DIMENSION}
          color={isActive ? 'white' : iconColor}
          fill={'transparent'}
        />

        <Text
          x={textX}
          y={textY}
          dy=".33em"
          fontSize={12}
          fontWeight={500}
          fill={textColor}
        >
          {runText}
        </Text>

        {hasChildren && (
          <g
            onClick={(e) => {
              e.stopPropagation();
              setFoldedRuns((prev) => {
                if (prev.includes(run.id)) {
                  return prev.filter((id) => id !== run.id);
                } else {
                  return [...prev, run.id];
                }
              });
              if (!hasChildrenVisible) {
                onClick();
              }
            }}
            style={{
              transform: `translateX(${collapseIconX}px)`,
              transition: 'transform 300ms ease-in-out',
            }}
          >
            <rect
              x={0}
              y={iconY - 2}
              width={GRAPH_RUN_ICON_DIMENSION + 4}
              height={GRAPH_RUN_ICON_DIMENSION + 4}
              rx={4}
              className="cursor-pointer fill-background transition-colors duration-300 ease-in-out hover:fill-slate-100 dark:hover:fill-slate-800"
              stroke={getCollapseIconStroke()}
            />
            <CollapseIcon
              x={2}
              y={iconY}
              width={GRAPH_RUN_ICON_DIMENSION}
              height={GRAPH_RUN_ICON_DIMENSION}
            />
          </g>
        )}
      </g>
    )
  );
}

export function WaterfallLatencyGraph({
  treeState,
  activeRun,
  onNavigate,
  setFoldedRuns,
  filteredRuns,
}: {
  treeState: ReturnType<typeof useRunsInTreeStructure>;
  activeRun?: RunSchema;
  onNavigate: (run: RunSchema) => void;
  setFoldedRuns: React.Dispatch<React.SetStateAction<string[]>>;
  filteredRuns?: RunSchema[];
}) {
  const { isDarkMode } = useColorScheme();
  const { tree, childRunLookupWithFolds } = treeState;

  const { flattenedRuns } = tree;

  const containerWidth = useWaterfallZoomState((state) => state.containerWidth);
  const setContainerWidth = useWaterfallZoomState(
    (state) => state.setContainerWidth
  );
  const zoomWidth = useWaterfallZoomState((state) => state.zoomWidth);
  const setZoomWidth = useWaterfallZoomState((state) => state.setZoomWidth);
  const resetZoom = useWaterfallZoomState((state) => state.resetZoom);

  const resizeHandler = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
        if (zoomWidth === 0) {
          setZoomWidth(entry.contentRect.width);
        }
      }
    },
    [setContainerWidth, setZoomWidth, zoomWidth]
  );
  const containerRef = useResizeObserver<HTMLDivElement>(resizeHandler);

  const { debouncedScrollLeft } = useScrollPosition(containerRef);

  const width = Math.max(zoomWidth, containerWidth);
  const height = flattenedRuns.length * TREE_COLLAPSED_NODE_HEIGHT;
  const paintableAreaWidth = width - 30;

  const minMaxTimeAtIdx = useMemo(
    () => calculateMinMaxTimeRanges(flattenedRuns),
    [flattenedRuns]
  );

  const { min: minStartTime, max: maxEndTime } = minMaxTimeAtIdx[0] ?? {};

  const xScale = scaleTime({
    domain: [new Date(minStartTime), new Date(maxEndTime)],
    range: [0, paintableAreaWidth],
  });

  const yScale = useMemo(
    () =>
      scaleOrdinal({
        domain: flattenedRuns.map((_, index) => index),
        range: flattenedRuns.map((_, index) => {
          return index * TREE_COLLAPSED_NODE_HEIGHT;
        }),
      }),
    [flattenedRuns]
  );

  const grayStroke = isDarkMode ? '#3F3F46' : '#E4E4E7';
  const grayText = isDarkMode ? '#A0A0AB' : '#70707B';

  const numTicks = Math.min(
    8,
    Math.max(Math.round((maxEndTime - minStartTime) / 1000), 2)
  );

  useEffect(() => {
    resetZoom();
  }, [flattenedRuns?.[0].run.id]);

  if (flattenedRuns.length === 0 && !filteredRuns?.length) {
    return null;
  }

  return maxEndTime - minStartTime === 0 ? (
    <div className="flex h-full w-full items-center justify-center">
      <div className="text-sm text-gray-500">Latency too small to graph</div>
    </div>
  ) : (
    <div
      ref={containerRef}
      className="relative w-full flex-1 overflow-x-auto overflow-y-scroll pl-4 pr-2"
    >
      <div className="sticky top-0 z-10 bg-background">
        <svg
          width={width}
          height={LATENCY_GRAPH_HEIGHT_TOP_OFFSET}
          className="bg-background"
        >
          <AxisTop
            stroke={grayStroke}
            tickStroke={grayStroke}
            tickLabelProps={{
              fill: grayText,
              fontSize: 12,
            }}
            scale={xScale}
            top={LATENCY_GRAPH_HEIGHT_TOP_OFFSET}
            tickFormat={(value) => {
              if (value instanceof Date) {
                if (
                  value.getTime() === minStartTime ||
                  value.getTime() === maxEndTime
                ) {
                  return '0s';
                }
                return `${Math.round(
                  (value.getTime() - minStartTime) / 1000
                )}s`;
              }
              return value.toString();
            }}
            numTicks={numTicks}
          />
        </svg>
      </div>
      <svg width={width} height={height}>
        <GridColumns
          scale={xScale}
          width={width}
          height={height}
          top={0}
          stroke={grayStroke}
          numTicks={numTicks}
        />

        {flattenedRuns.map(({ run, isActiveDescendant }, index) => {
          const isActive = run.id === activeRun?.id;
          const childRuns = childRunLookupWithFolds?.[run.id];
          const hasChildren = Array.isArray(childRuns) && index !== 0;
          const hasChildrenVisible = hasChildren && childRuns?.length > 0;
          const { min: minStartTime, max: maxEndTime } = minMaxTimeAtIdx[index];

          const isFiltered = Boolean(
            filteredRuns?.find((r) => r.id === run.id)
          );
          return (
            <WaterfallLatencyBar
              key={run.id}
              onClick={() => onNavigate(run)}
              run={run}
              index={index}
              xScale={xScale}
              yScale={yScale}
              isActive={isActive}
              isFiltered={isFiltered}
              isActiveDescendant={isActiveDescendant}
              minStartTime={minStartTime}
              maxEndTime={maxEndTime}
              svgWidth={width}
              containerWidth={containerWidth}
              hasChildren={hasChildren}
              setFoldedRuns={setFoldedRuns}
              hasChildrenVisible={hasChildrenVisible}
              debouncedScrollLeft={debouncedScrollLeft}
            />
          );
        })}
      </svg>
    </div>
  );
}
